<script setup lang="ts">
import dayjs, { Dayjs } from 'dayjs'
import { computed, nextTick, onMounted, type PropType, ref, watch } from 'vue'
import { QField } from 'quasar'
import { Color } from '@previo/codebooks'
import { useDateTimeRangeValidator, useTimeRangeValidator } from '../../composables/rules'
import LabelInputField from '../../../components/LabelInputField.vue'
import PIcon from '../../../../icon/PIcon.vue'
import { PTimeInputFieldIncrement } from '../../../types'
import { type PValidationRule } from '../../../../../types/types'
import { usePrevioDS } from '../../../../../composables/helpers'
import { ErrorState } from '../segments/model/ErrorState'
import FormatSegmentBuilder from '../segments/FormatSegmentBuilder.vue'

const props = defineProps({
  label: {
    type: String,
    default: null,
  },
  tooltip: {
    type: String,
    default: null,
  },
  hint: {
    type: String,
    default: undefined,
  },
  timeIncrement: {
    type: Number as PropType<PTimeInputFieldIncrement>,
    default: PTimeInputFieldIncrement.QUARTER,
  },
  rules: {
    type: Array as PropType<PValidationRule[]>,
    default: () => [],
  },
  min: {
    type: Object as PropType<Dayjs>,
    required: false,
  },
  max: {
    type: Object as PropType<Dayjs>,
    required: false,
  },
  disable: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disableClearable: {
    type: Boolean,
    default: false,
  },
  hideTimeField: {
    type: Boolean,
    default: false,
  },
  autofocus: {
    type: Boolean,
    default: false,
  },
  includeTimeValidation: {
    type: Boolean,
    default: false,
  },
  defaultTime: {
    type: String,
    validator: (value?: string): boolean => {
      if (!value) return true
      return dayjs(value, 'HH:mm', true).isValid()
    },
    required: false,
  },
  format: {
    type: String,
    default: undefined,
  },
  cy: {
    type: String,
    required: true,
  },
})

type Emits = {
  (e: 'open-picker'): void
  (e: 'clear'): void
}

const emits = defineEmits<Emits>()

const ds = usePrevioDS()

const model = defineModel<Dayjs | null>()
const qFieldComponent = ref<InstanceType<typeof QField>>()

const validatorModel = computed<Dayjs | null>(() => model.value ?? null)

const isRequired = computed<boolean>(() => {
  return props.rules.includes(ds.rules.required)
})

const validateDateTimeRange = useDateTimeRangeValidator(
  validatorModel,
  !props.hideTimeField,
  props.min,
  props.max,
)
const validateTimeRange = useTimeRangeValidator(validatorModel, props.min, props.max)

const errorState = ref<ErrorState>(new ErrorState())

const qRules = computed<PValidationRule[]>(() => {
  const rules = [...props.rules]
  if (model.value !== null) {
    rules.push(validateDateTimeRange)
  }

  if (props.includeTimeValidation) {
    rules.push(validateTimeRange)
  }

  if (errorState.value.hasError) {
    const message = errorState.value.errorMessage
    rules.push(() => message)
  }

  return rules
})

const qError = ref<boolean>(false)
const qErrorMessage = ref<string>('')

const focus = (): void => {
  qFieldComponent.value?.$el.classList.add('q-field--highlighted')
}

const unfocus = (): void => {
  qFieldComponent.value?.$el.classList.remove('q-field--highlighted')
}

const onClick = (e: PointerEvent): void => {
  if (props.disable || props.readonly) {
    return
  }

  const target = e.target as HTMLElement

  if (target.classList.contains('q-icon')) {
    return
  }

  emits('open-picker')
}

watch(
  errorState,
  () => {
    qError.value = errorState.value.hasError
    qErrorMessage.value = errorState.value.errorMessage
  },
  { deep: true },
)

onMounted(() => {
  qFieldComponent.value?.$el.addEventListener('click', onClick)

  if (!props.autofocus || props.readonly) {
    return
  }

  nextTick(() => {
    emits('open-picker')
  })
})

defineExpose({
  focus,
  unfocus,
})
</script>

<template>
  <label-input-field :label="label" :is-required="isRequired" :tooltip="tooltip" />

  <q-field
    ref="qFieldComponent"
    v-model="model"
    :bg-color="Color.WHITE"
    :rules="qRules"
    :autofocus="autofocus"
    :disable="disable"
    :hint="hint"
    :data-cy="cy"
    :readonly="readonly || ds.screen().isMobile"
    :class="{ 'ds-input-field--readonly': readonly }"
    :error="qError"
    :error-message="qErrorMessage"
    outlined
    no-error-icon
    hide-bottom-space
  >
    <template #default>
      <format-segment-builder
        v-model="model"
        v-model:error-state="errorState"
        :hide-time-field="hideTimeField"
        :default-time="defaultTime"
        :disable-clearable="disableClearable"
        :readonly="readonly"
        :format="format"
      />

      <slot name="context-menu" />
    </template>

    <template #append>
      <template v-if="!disableClearable && model !== null && !readonly">
        <p-icon icon="xmark-large" cursor-pointer :size="15" @click="$emit('clear')" />
      </template>
    </template>
  </q-field>
</template>
